import React from "react";
import { markupChain } from "../../structure/markups";

const SAMPLE_DIRECT_COST = 100000;
const money = (v) => `${Math.round(v).toLocaleString("en-US").replace(/,/g, " ")} ₾`;

function Seg({ value, options, onChange }) {
  return (
    <div className="pseg">
      {options.map((o) => (
        <button key={o.value} type="button" className={value === o.value ? "on" : ""} onClick={() => onChange(o.value)}>
          {o.label}
          {o.sub && <small>{o.sub}</small>}
        </button>
      ))}
    </div>
  );
}

function Switch({ on, onChange, children }) {
  return (
    <button type="button" className={`swb${on ? " on" : ""}`} onClick={() => onChange(!on)}>
      <span className="sw" />
      {children}
    </button>
  );
}

export default function BudgetingStep({ settings, onChange, objectCount, onFreeze }) {
  const set = (patch) => onChange({ ...settings, ...patch });
  const markups = settings.markups || [];
  const setMarkup = (i, patch) => set({ markups: markups.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const sheet = markupChain(SAMPLE_DIRECT_COST, markups, "sheet");
  const total = markupChain(sheet.total, markups, "total");

  return (
    <div className="bgrid">
      <div className="bcol">
        <section className="bcard">
          <div className="bcard-h">
            <h4>Excel-ის ფორმატი</h4>
            <p>სვეტები, ფურცლები და ნომრები</p>
          </div>
          <div className="bopt">
            <span className="bopt-l">დეტალიზაცია</span>
            <Seg
              value={settings.detail || "detailed"}
              onChange={(detail) => set({ detail })}
              options={[
                { value: "detailed", label: "დეტალური", sub: "რესურსები სამუშაოს ქვეშ" },
                { value: "tender", label: "სატენდერო", sub: "მასალა / ხელფასი / მექანიზმი სამუშაოს ხაზზე" },
              ]}
            />
          </div>
          {objectCount > 1 && (
            <div className="bopt">
              <span className="bopt-l">ფურცლების თანმიმდევრობა</span>
              <Seg
                value={settings.order || "object"}
                onChange={(order) => set({ order })}
                options={[
                  { value: "object", label: "ობიექტებით", sub: "1-TOTAL, 1-1.0 … 2-TOTAL …" },
                  { value: "discipline", label: "დისციპლინებით", sub: "1-2.2, 2-2.2 …" },
                ]}
              />
            </div>
          )}
          <div className="bopt">
            <span className="bopt-l">
              ინგლისური სვეტი <small>„Work Description“ ქართულის გვერდით, სათაურები ორენოვანი</small>
            </span>
            <Switch on={settings.english !== false} onChange={(english) => set({ english })}>
              {settings.english !== false ? "ჩართულია" : "გამორთულია"}
            </Switch>
          </div>
          <div className="bopt">
            <span className="bopt-l">
              ნომრები <small>{settings.frozen ? "დაფიქსირებულია: ახალი ხაზი იღებს შემდეგ თავისუფალ ნომერს" : "ავტომატური: ხაზები ნომრდება რიგით"}</small>
            </span>
            <Switch on={!!settings.frozen} onChange={onFreeze}>
              {settings.frozen ? "🔒 დაფიქსირებული" : "ავტომატური"}
            </Switch>
          </div>
        </section>

        <section className="bcard">
          <div className="bcard-h">
            <h4>% დანარიცხები</h4>
            <p>როგორც შაბლონში: ზედნადები და მოგება — ყოველი Hard Cost ფურცლის ბოლოს, დ.ღ.გ. — TOTAL-ზე. თითო წინა ჯამზე.</p>
          </div>
          <div className="mkl">
            <div className="mkr h">
              <span />
              <span>დასახელება</span>
              <span>სად იწერება</span>
              <span>%</span>
            </div>
            {markups.map((m, i) => (
              <div key={m.k || i} className={`mkr${m.on ? "" : " off"}`}>
                <input type="checkbox" checked={!!m.on} onChange={(e) => setMarkup(i, { on: e.target.checked })} aria-label="ჩართვა" />
                <input className="inp" value={m.name} onChange={(e) => setMarkup(i, { name: e.target.value })} />
                <Seg
                  value={m.at}
                  onChange={(at) => setMarkup(i, { at })}
                  options={[
                    { value: "sheet", label: "ფურცლის ბოლოს" },
                    { value: "total", label: "TOTAL-ზე" },
                  ]}
                />
                <input className="inp num" value={m.pct} inputMode="decimal" onChange={(e) => setMarkup(i, { pct: e.target.value })} />
              </div>
            ))}
          </div>
          <button
            type="button"
            className="linkbtn"
            onClick={() => set({ markups: [...markups, { k: `m${Date.now().toString(36)}`, name: "ახალი დანარიცხი", pct: 0, on: true, at: "total" }] })}
          >
            + დანარიცხი
          </button>
          <div className="bopt">
            <span className="bopt-l">ხარჯთაღრიცხვაში</span>
            <Seg
              value={settings.inUnit ? "unit" : "lines"}
              onChange={(v) => set({ inUnit: v === "unit" })}
              options={[
                { value: "lines", label: "ცალკე ხაზებად", sub: "ფურცლის ბოლოს და TOTAL-ზე" },
                { value: "unit", label: "ფასში ჩაშენებული", sub: "ერთეულის ფასები მოიცავს დანარიცხებს" },
              ]}
            />
          </div>
        </section>
      </div>

      <div className="bcol">
        <section className="bcard">
          <div className="bcard-h">
            <h4>Excel-ში ასე გამოჩნდება</h4>
            <p>ნიმუში: პირდაპირი ხარჯი {money(SAMPLE_DIRECT_COST)}</p>
          </div>
          <div>
            <div className="pcap">Hard Cost ფურცლის ბოლო</div>
            <PreviewLines lines={settings.inUnit ? [{ kind: "sum", name: "ჯამი (ფასებში დანარიცხებით)", amount: sheet.total }] : sheet.lines} />
          </div>
          <div>
            <div className="pcap">TOTAL-ის ბოლო</div>
            <PreviewLines lines={total.lines} />
          </div>
          <div className="snote">Soft Cost და OPEX ფურცლებს დანარიცხები არ აქვთ — მხოლოდ ჯამი.</div>
        </section>
      </div>
    </div>
  );
}

function PreviewLines({ lines }) {
  return (
    <div className="ptot" data-no-translate="true">
      {lines.map((l, i) => (
        <div key={i} className={l.kind === "sum" ? "sum" : "mk"}>
          <span />
          <span>{l.kind === "markup" ? `${l.name} ${l.pct}%` : l.name}</span>
          <span className="r">{money(l.amount)}</span>
        </div>
      ))}
    </div>
  );
}
